import type { Stage } from './stage'
import type { Ticket } from './tickets'
import type { Lane } from './lanes'
import type { Component, ComponentGroup } from './components'
import type { LinearTicket } from './linearTickets'
import type { ActivityRow } from './activity'
import { severityLine, type ProjectFindings } from './findings'
import { usd } from '../../lib/money'

/**
 * The one line a closed section shows about what is inside it.
 *
 * Every line here is a count of rows already on the page, said in words. None of
 * them rates, ranks or flags: "2 failed" is a fact in the same muted type as
 * "3 completed", never a badge (product rule 1), and a count of zero is left out
 * rather than printed, so a quiet lane group does not read as a list of noughts.
 */

/** "1 lane", "3 lanes". */
export function count(n: number, noun: string, plural = `${noun}s`): string {
  return `${n} ${n === 1 ? noun : plural}`
}

/**
 * "2 running · 1 awaiting approval" -- how many are on each stage, in the order
 * the track runs, lower-cased because it sits inside a sentence.
 */
export function stageTally(stages: ReadonlyArray<Stage>): string {
  const byLabel = new Map<string, { position: number; n: number }>()
  for (const s of stages) {
    const held = byLabel.get(s.label)
    if (held) held.n += 1
    else byLabel.set(s.label, { position: s.position, n: 1 })
  }
  return [...byLabel.entries()]
    .sort(([, a], [, b]) => a.position - b.position)
    .map(([label, { n }]) => `${n} ${label.toLowerCase()}`)
    .join(' · ')
}

/** A department's lanes: how many, and what stage each is on. */
export function laneGroupLine(lanes: ReadonlyArray<Lane>): string {
  const tally = stageTally(lanes.map((l) => l.stage))
  return tally ? `${count(lanes.length, 'lane')} · ${tally}` : count(lanes.length, 'lane')
}

/** Departments in first-seen order, each with its lanes. `toLanes` already sorts by department. */
export function lanesByDepartment(lanes: ReadonlyArray<Lane>): { department: string; lanes: Lane[] }[] {
  const groups = new Map<string, Lane[]>()
  for (const lane of lanes) {
    const held = groups.get(lane.department)
    if (held) held.push(lane)
    else groups.set(lane.department, [lane])
  }
  return [...groups.entries()].map(([department, list]) => ({ department, lanes: list }))
}

export interface RepoGroup<T> {
  /** `owner/name` as the run rows carry it, or null for tickets with no repo recorded. */
  repo: string | null
  /** The part after the slash: what the section is headed with. */
  name: string
  /** The part before it, when there is one. */
  owner: string | null
  tickets: T[]
}

/**
 * Tickets split by the repo their runs went to, which is one per Linear project
 * through the repo routing. Groups keep the order the tickets arrive in, so the
 * repo with the most recent activity comes first; tickets with no repo go last,
 * under a heading that says so rather than being dropped.
 */
export function ticketsByRepo<T extends { repo: string | null }>(tickets: ReadonlyArray<T>): RepoGroup<T>[] {
  const groups = new Map<string | null, T[]>()
  for (const ticket of tickets) {
    const held = groups.get(ticket.repo)
    if (held) held.push(ticket)
    else groups.set(ticket.repo, [ticket])
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === null ? 1 : 0) - (b === null ? 1 : 0))
    .map(([repo, list]) => {
      const slash = repo ? repo.indexOf('/') : -1
      return {
        repo,
        name: repo === null ? 'No repo recorded' : slash >= 0 ? repo.slice(slash + 1) : repo,
        owner: repo !== null && slash > 0 ? repo.slice(0, slash) : null,
        tickets: list,
      }
    })
}

/** A ticket list: how many, where they stand, and what they cost where anything recorded one. */
export function ticketsLine(tickets: ReadonlyArray<Pick<Ticket, 'stage' | 'spend' | 'hasCost'>>): string {
  const parts = [count(tickets.length, 'ticket')]
  const tally = stageTally(tickets.map((t) => t.stage))
  if (tally) parts.push(tally)
  if (tickets.some((t) => t.hasCost)) {
    const spend = tickets.reduce((sum, t) => sum + (t.hasCost ? t.spend : 0), 0)
    parts.push(`${usd(spend, { precise: spend < 1 })} spent`)
  }
  return parts.join(' · ')
}

/**
 * What a component touches, as counts: "reads 3 · writes 1 · sends nothing".
 * Each list is named even when empty, because "sends nothing" is the answer the
 * directory exists to give and leaving it off would read as not recorded.
 */
export function componentLine(component: Pick<Component, 'touches' | 'secrets'>): string {
  const parts = component.touches.map((list) =>
    list.entries.length === 0
      ? `${list.label.toLowerCase()} nothing`
      : `${list.label.toLowerCase()} ${list.entries.length}`,
  )
  if (component.secrets.length > 0) parts.push(count(component.secrets.length, 'secret'))
  return parts.join(' · ')
}

/** A kind heading: how many components, and any that are not live. */
export function componentGroupLine(group: Pick<ComponentGroup, 'components'>): string {
  const byStatus = new Map<string, number>()
  for (const c of group.components) byStatus.set(c.status, (byStatus.get(c.status) ?? 0) + 1)
  const parts = [count(group.components.length, 'component')]
  // Only worth saying when they are not all the same.
  if (byStatus.size > 1) parts.push([...byStatus.entries()].map(([status, n]) => `${n} ${status}`).join(', '))
  return parts.join(' · ')
}

/** Open Linear tickets: how many, and how many of them Linear marks urgent or high. */
export function linearTicketsLine(tickets: ReadonlyArray<Pick<LinearTicket, 'priority'>>): string {
  const pressing = tickets.filter((t) => t.priority === 1 || t.priority === 2).length
  const parts = [`${tickets.length} open`]
  if (pressing > 0) parts.push(`${pressing} urgent or high`)
  return parts.join(' · ')
}

/**
 * What a project's card holds below its head: "2 open (1 critical · 1 medium) ·
 * 1 accepted · 3 closed". The severities are the open list's, so they sit with
 * its count rather than beside it as a second figure to reconcile.
 */
export function projectContentsLine(p: Pick<ProjectFindings, 'board'>): string {
  const { open, accepted, closed, openBySeverity } = p.board
  const rated = severityLine(openBySeverity)
  const parts = [
    open.length === 0 ? 'nothing open'
    : rated === 'nothing open' ? `${open.length} open`
    : `${open.length} open (${rated})`,
  ]
  if (accepted.length > 0) parts.push(`${accepted.length} accepted`)
  if (closed.length > 0) parts.push(`${closed.length} closed`)
  return parts.join(' · ')
}

export interface ActivityDay {
  date: string
  isToday: boolean
  rows: ActivityRow[]
}

/** The feed, newest first, split where the date changes. Order inside a day is untouched. */
export function activityDays(rows: ReadonlyArray<ActivityRow>): ActivityDay[] {
  const days: ActivityDay[] = []
  for (const row of rows) {
    const last = days[days.length - 1]
    if (last && last.date === row.date) last.rows.push(row)
    else days.push({ date: row.date, isToday: row.isToday, rows: [row] })
  }
  return days
}

/** "42 events · 5 milestones", milestones only where the registry marked any. */
export function activityDayLine(day: Pick<ActivityDay, 'rows'>): string {
  const milestones = day.rows.filter((r) => r.isMilestone).length
  const parts = [count(day.rows.length, 'event')]
  if (milestones > 0) parts.push(count(milestones, 'milestone'))
  return parts.join(' · ')
}
